import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Sparkles, Send, User, MailCheck, X, AlertCircle } from "lucide-react";

const SUGGESTIONS = [
  "Fais-moi le point sur mon activité",
  "Quels devis dois-je relancer ?",
  "Qu'est-ce qui mérite mon attention aujourd'hui ?",
];

const ERROR_MSG =
  "Nenzio rencontre un problème pour accéder aux données. Réessaie dans quelques instants.";

export default function Nenzio() {
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  async function send(text) {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    const nextMessages = [...messages, { role: "user", content: message }];
    setMessages(nextMessages);
    setInput("");
    setLoading(true);
    try {
      // On n'envoie que le texte des derniers échanges (jamais de données métier).
      const history = nextMessages.slice(-6).map((m) => ({ role: m.role, content: m.content }));
      const r = await api.post("/nenzio/chat", { message, history });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: r.data.reply, action: r.data.action || null },
      ]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMessages((m) => [
        ...m,
        { role: "assistant", content: detail || ERROR_MSG, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  }

  function euro(v) {
    if (v === null || v === undefined) return null;
    try {
      return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);
    } catch {
      return `${v} €`;
    }
  }

  function updateMsg(idx, patch) {
    setMessages((list) => list.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
  }

  async function sendReminder(idx, quoteNumber) {
    updateMsg(idx, { reminderState: "sending" });
    try {
      const r = await api.post("/nenzio/actions/send-quote-reminder", { quote_number: quoteNumber });
      updateMsg(idx, { reminderState: "sent" });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: r.data.message || "Relance envoyée avec succès." },
      ]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Impossible d'envoyer la relance pour le moment.";
      updateMsg(idx, { reminderState: "error", reminderError: detail });
    }
  }

  function cancelReminder(idx) {
    updateMsg(idx, { reminderState: "cancelled" });
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="nenzio-page">
      {/* En-tête */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 bg-gradient-to-br from-[#16324F] to-[#19C3B3] text-white shadow-sm">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#16324F] leading-tight">
            Nenzio
          </h1>
          <p className="text-[#19C3B3] font-semibold text-sm">Votre copilote IA</p>
          <p className="mt-1 text-slate-500 text-sm max-w-lg">
            Analysez votre activité et identifiez rapidement ce qui mérite votre attention.
          </p>
        </div>
      </div>

      {/* Conversation */}
      <div className="mt-6 kf-card p-0 overflow-hidden flex flex-col" style={{ minHeight: "52vh" }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4" data-testid="nenzio-thread">
          {messages.length === 0 && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="nenzio-empty">
              <div className="w-12 h-12 rounded-full bg-[#EAF1F7] flex items-center justify-center text-[#16324F]">
                <Sparkles className="w-6 h-6" />
              </div>
              <p className="mt-3 text-slate-600 font-medium">Posez une question à Nenzio</p>
              <p className="text-slate-400 text-sm max-w-xs mt-1">
                Il s&apos;appuie uniquement sur vos données MonTempsGagné.
              </p>
            </div>
          )}

          {messages.map((m, i) => (
            <div
              key={i}
              className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}
              data-testid={`nenzio-msg-${m.role}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  m.role === "user"
                    ? "bg-[#16324F] text-white"
                    : "bg-gradient-to-br from-[#16324F] to-[#19C3B3] text-white"
                }`}
              >
                {m.role === "user" ? <User className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              </div>
              <div className="max-w-[85%] flex flex-col gap-2">
                <div
                  className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#16324F] text-white rounded-tr-sm"
                      : m.error
                      ? "bg-[#FFF1ED] text-[#B23A1F] rounded-tl-sm"
                      : "bg-[#F1F5F9] text-slate-800 rounded-tl-sm"
                  }`}
                >
                  {m.content}
                </div>

                {/* Carte de confirmation de relance */}
                {m.action?.type === "quote_reminder" && m.reminderState !== "cancelled" && (
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="nenzio-reminder-card">
                    <div className="flex items-center gap-2">
                      <MailCheck className="w-4 h-4 text-[#19C3B3]" />
                      <span className="font-display font-bold text-[#16324F] text-sm">
                        {m.reminderState === "sent" ? "Relance envoyée" : "Relance prête"}
                      </span>
                    </div>
                    <div className="mt-3 space-y-1 text-sm">
                      {m.action.preview.client && (
                        <div><span className="text-slate-400">Client : </span><span className="text-[#16324F] font-medium">{m.action.preview.client}</span></div>
                      )}
                      {m.action.preview.vehicule && (
                        <div><span className="text-slate-400">Véhicule : </span><span className="text-[#16324F]">{m.action.preview.vehicule}</span></div>
                      )}
                      <div>
                        <span className="text-slate-400">Devis : </span>
                        <span className="text-[#16324F]">{m.action.preview.quote_number}</span>
                        {euro(m.action.preview.montant) && <span className="text-[#16324F] font-medium"> · {euro(m.action.preview.montant)}</span>}
                      </div>
                      {m.action.preview.email && (
                        <div><span className="text-slate-400">Destinataire : </span><span className="text-[#16324F]">{m.action.preview.email}</span></div>
                      )}
                    </div>

                    <div className="mt-3">
                      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Objet</div>
                      <div className="text-sm text-[#16324F]">{m.action.preview.objet}</div>
                      <div className="mt-2 rounded-lg bg-[#F6F9FC] border border-slate-100 p-3 text-sm text-slate-700 whitespace-pre-wrap" data-testid="nenzio-reminder-preview">
                        {m.action.preview.message}
                      </div>
                    </div>

                    {!m.action.can_send && (
                      <div className="mt-3 flex items-start gap-2 text-xs text-[#B23A1F] bg-[#FFF1ED] rounded-lg p-2" data-testid="nenzio-reminder-blocked">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{m.action.reason === "no_email" ? "Aucune adresse email disponible pour ce client — envoi impossible." : "Envoi indisponible pour ce devis."}</span>
                      </div>
                    )}

                    {m.reminderState === "error" && (
                      <div className="mt-3 flex items-start gap-2 text-xs text-[#B23A1F] bg-[#FFF1ED] rounded-lg p-2">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{m.reminderError}</span>
                      </div>
                    )}

                    {m.action.can_send && m.reminderState !== "sent" && (
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={() => sendReminder(i, m.action.preview.quote_number)}
                          disabled={m.reminderState === "sending"}
                          className="flex-1 h-10 rounded-xl bg-[#16324F] text-white text-sm font-semibold hover:bg-[#0f2438] transition-base disabled:opacity-50"
                          data-testid="nenzio-reminder-send"
                        >
                          {m.reminderState === "sending" ? "Envoi…" : "Envoyer la relance"}
                        </button>
                        <button
                          onClick={() => cancelReminder(i)}
                          disabled={m.reminderState === "sending"}
                          className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-base disabled:opacity-50 inline-flex items-center gap-1"
                          data-testid="nenzio-reminder-cancel"
                        >
                          <X className="w-4 h-4" /> Annuler
                        </button>
                      </div>
                    )}

                    {m.reminderState === "sent" && (
                      <div className="mt-3 text-sm text-[#0F7A70] font-medium">✓ Relance envoyée avec succès.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex gap-3" data-testid="nenzio-loading">
              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br from-[#16324F] to-[#19C3B3] text-white">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="rounded-2xl rounded-tl-sm px-4 py-3 bg-[#F1F5F9]">
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Suggestions */}
        {messages.length === 0 && (
          <div className="px-4 md:px-6 pb-3 flex flex-wrap gap-2" data-testid="nenzio-suggestions">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                disabled={loading}
                className="text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-[#19C3B3] hover:text-[#16324F] transition-base disabled:opacity-50"
                data-testid="nenzio-suggestion"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Saisie */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="border-t border-slate-100 p-3 flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder="Écrivez votre question…"
            className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#19C3B3] focus:ring-2 focus:ring-[#19C3B3]/20 max-h-32"
            data-testid="nenzio-input"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="h-11 w-11 rounded-xl bg-[#16324F] text-white flex items-center justify-center shrink-0 hover:bg-[#0f2438] transition-base disabled:opacity-40"
            data-testid="nenzio-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>

      <p className="mt-3 text-xs text-slate-400 text-center">
        Nenzio analyse uniquement vos données et ne réalise encore aucune action.
      </p>
    </div>
  );
}
